import * as vp from 'vopay';

import React, { useState, useEffect, useMemo } from 'react';
import { withRouter, useLocation, RouteComponentProps } from 'react-router-dom';

import DashboardNavTop from '@/components/UI/DashboardNavTop';

import NavLeftContainer from '@/components/UI/NavLeftContainer/NavLeftContainer';

import DashboardRoutes from '@/components/Homepage/Routes';
import UsersRoutes from '@/components/Users/Route';
import AccountsContainer from '@/components/Accounts/AccountsContainer';
import BillingPackageRoutes from '@/components/BillingPackages/Routes';
import ReportRoutes from '@/components/Reports/Routes';
import StatementRoutes from '@/components/Statements/Routes';
import LeadsRoutes from '@/components/Leads/Routes';
import PartnersRoutes from '@/components/Partners/Routes';
import SupportRoutes from '@/components/Support/Routes';
import SettingsRoutes from '@/components/Settings/Routes';
import OnboardingFormTrackerRoutes from '@/components/OnBoardingFormTracker/Routes';

import TransactionsRoutes from '@/components/Transactions/Routes';
import { useBillingPackages } from '@/providers/BillingPackages.provider';
import { useBranding } from '@/providers/Branding.provider';
import { useHubSpotDropdownDataSet } from '../providers/HubSpotDropdownData.provider';
import useActiveModule from '@/hooks/useActiveModule';
import styles from './Footer.module.scss';
import navBarStyles from './NavBar.module.scss';
import useFetchAccountBranding from '@/hooks/useApi/useFetchAccountBranding';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useHubspotElement from '@/hooks/useHubspotElement';

const { Container, DashboardLayout } = vp;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Dashboard: React.FunctionComponent<RouteComponentProps> = (_props: RouteComponentProps) => {
  const { pathname } = useLocation();
  const { activeModule } = useActiveModule();
  const { refreshBillingPackages } = useBillingPackages();
  const { refreshHubSpotDropdownData } = useHubSpotDropdownDataSet();
  const { fetchAccountBranding } = useFetchAccountBranding();
  const { logo, smallLogo, setBrandingValues } = useBranding();
  const [expandMenu, setExpandMenu] = useState(false);
  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const { response } = usePartnerData();
  const { navRef } = useHubspotElement();

  useEffect(() => {
    refreshBillingPackages({ useCached: true });
    refreshHubSpotDropdownData({ useCached: true });
  }, []);

  useEffect(() => {
    vp.loadScriptByURL('hs-script-loader', '//js.hs-scripts.com/6101456.js')
      .then(() => {
        console.log('HubSpot script loaded');
      })
      .catch((error) => {
        console.error('Failed to load HubSpot script:', error);
      });
  }, []);

  useEffect(() => {
    initBranding();
  }, [response.accountId]);

  async function initBranding() {
    setBrandingLoaded(false);
    const res = await fetchAccountBranding();

    if (!res.Success) {
      console.warn(res.ErrorMessage);
    }

    setBrandingValues({
      primaryColor: res.BrandingDetails.PrimaryColor,
      secondaryColor: res.BrandingDetails.SecondaryColor,
      logo: res.Logo,
      smallLogo: res.SmallLogo,
      appTitle: res.AppTitle,
      appFavicon: res.Favicon,
    });

    setBrandingLoaded(true);
  }

  const handlers = {
    onMouseEnter: (show: boolean) => {
      setExpandMenu(show);
    },
  };
  const isEmbedded = useMemo(() => pathname.split('/')[1] === 'onboarding-form-tracker', [pathname]);

  if (!brandingLoaded) return null;

  return (
    <DashboardLayout
      handlers={handlers}
      pathname={pathname}
      isEmbedded={isEmbedded}
      expandMenu={expandMenu}
      navSubTop={
        <div className={navBarStyles.navbarContainer} ref={navRef}>
          <DashboardNavTop />
        </div>
      }
      navLeft={
        <NavLeftContainer
          logo={logo === '' ? undefined : logo}
          smallLogo={smallLogo === '' ? undefined : smallLogo}
          expandMenu={expandMenu}
        />
      }
      content={
        <Container maxWidth="lg" className={`${styles.mainContent} ${isEmbedded && styles.embedded}`}>
          {activeModule === 'dashboard' && <DashboardRoutes />}
          {activeModule === 'partners' && <PartnersRoutes />}
          {activeModule === 'users' && <UsersRoutes />}
          {activeModule === 'accounts' && <AccountsContainer />}
          {activeModule === 'leads' && <LeadsRoutes />}
          {activeModule === 'billingPackages' && <BillingPackageRoutes />}
          {activeModule === 'reports' && <ReportRoutes />}
          {activeModule === 'statements' && <StatementRoutes />}
          {activeModule === 'support' && <SupportRoutes />}
          {activeModule === 'transactions' && <TransactionsRoutes />}
          {activeModule === 'settings' && <SettingsRoutes />}
          {activeModule === 'onBoardingFormTracker' && <OnboardingFormTrackerRoutes />}
        </Container>
      }
      footer={
        window.location.hostname.includes('vopay') || window.location.hostname.includes('localhost') ? (
          <div className={styles.footerContainer}>
            <div className={styles.container}>
              <p>
                ©VoPay International Inc. {new Date().getFullYear()} All Rights Reserved |
                <a href="https://vopay.com/legal/services/tos/"> Terms of Use</a> |
                <a href="https://vopay.com/legal/services/privacy-policy/"> Privacy Policy</a>
              </p>
            </div>
          </div>
        ) : (
          <></>
        )
      }
    />
  );
};

export default withRouter(Dashboard);
